import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SiteLogo from '../../components/Layout/SiteLogo';
import LanguageSwitcher from '../../components/Layout/LanguageSwitcher';
import { useSettings } from '../../contexts/SettingsContext';
import { Button } from '../../components/UI';

type DocumentoTipo = 'passaporte' | 'cpf' | 'nif';

interface ContratoFormData {
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  nacionalidade: string;
  documentoTipo: DocumentoTipo;
  passaporteNumero: string;
  passaporteValidade: string;
  email: string;
  telefone: string;
  endereco: {
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    cep: string;
    pais: string;
  };
  consentLGPD: boolean;
}

const emptyFormData: ContratoFormData = {
  nome: '',
  sobrenome: '',
  dataNascimento: '',
  nacionalidade: '',
  documentoTipo: 'passaporte',
  passaporteNumero: '',
  passaporteValidade: '',
  email: '',
  telefone: '',
  endereco: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    cep: '',
    pais: 'Portugal'
  },
  consentLGPD: false
};

const pageShell = 'min-h-[100dvh] bg-slate-100';

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required
}) => (
  <span className="visa-form-label">
    {children}
    {required && <span className="text-red-500"> *</span>}
  </span>
);

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}> = ({ label, value, onChange, type = 'text', required, autoComplete }) => (
  <label className="visa-form-field block">
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      className={`visa-form-input ${type === 'date' ? 'visa-form-input--date' : ''}`}
    />
  </label>
);

const ContratoFormFill: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [formData, setFormData] = useState<ContratoFormData>(emptyFormData);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [done, setDone] = useState(false);
  const [modelo, setModelo] = useState<'judicial' | 'vistos' | null>(null);
  const displaySiteName = settings?.site?.name || t('common.siteNameFallback');

  useEffect(() => {
    if (!token) {
      setErrorMsg(t('contratoForm.linkInvalid'));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/contrato/public/fill/${token}`);
        setValid(res.data.valid);
        if (res.data.modelo === 'judicial' || res.data.modelo === 'vistos') {
          setModelo(res.data.modelo);
        }
        if (res.data.lang) {
          i18n.changeLanguage(res.data.lang);
        }
      } catch (err: unknown) {
        setValid(false);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('contratoForm.linkExpired');
        setErrorMsg(msg);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const patch = <K extends keyof ContratoFormData>(field: K, value: ContratoFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const patchEndereco = (field: keyof ContratoFormData['endereco'], value: string) => {
    setFormData((prev) => ({ ...prev, endereco: { ...prev.endereco, [field]: value } }));
  };

  const validate = (): string | null => {
    if (!formData.nome.trim() || !formData.sobrenome.trim()) return t('contratoForm.errors.required');
    if (!formData.dataNascimento) return t('contratoForm.errors.required');
    if (!formData.passaporteNumero.trim()) return t('contratoForm.errors.required');
    if (formData.documentoTipo === 'passaporte' && !formData.passaporteValidade) {
      return t('contratoForm.errors.validadeRequired');
    }
    if (!formData.email.trim()) return t('contratoForm.errors.required');
    const end = formData.endereco;
    if (!end.logradouro.trim() || !end.numero.trim() || !end.bairro.trim() || !end.cidade.trim() || !end.cep.trim()) {
      return t('contratoForm.errors.required');
    }
    if (!formData.consentLGPD) return t('contratoForm.errors.consent');
    return null;
  };

  const handleSubmit = async () => {
    setFormError('');
    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`/contrato/public/fill/${token}/submit`, {
        ...formData,
        passaporteValidade: formData.documentoTipo === 'passaporte' ? formData.passaporteValidade : undefined,
        consentLGPD: true
      });
      setDone(true);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('contratoForm.submitError');
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('contratoForm.linkUnavailable')}</h1>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className={`flex items-center justify-center p-4 sm:p-6 ${pageShell}`}>
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <CheckCircle className="w-14 h-14 text-primary-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('contratoForm.successTitle')}</h1>
          <p className="text-gray-600">{t('contratoForm.successMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}>
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
        <header className="text-center text-gray-900 mb-5 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <SiteLogo size="lg" nameClassName="font-bold text-gray-900 text-xl sm:text-2xl" className="justify-center" />
          </div>
          {modelo && (
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-primary-700 mb-1">
              {t(`contratoForm.modelo.${modelo}`)}
            </p>
          )}
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight px-1">{t('contratoForm.title')}</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-2 max-w-lg mx-auto px-2">{t('contratoForm.subtitle')}</p>
          <div className="flex justify-center mt-4 lg:hidden">
            <LanguageSwitcher compact variant="floating" className="shadow-md" />
          </div>
        </header>

        <div className="flex flex-col items-stretch lg:flex-row lg:items-start lg:justify-center lg:gap-5 xl:gap-8">
          <div className="w-full max-w-2xl mx-auto lg:mx-0 min-w-0 flex-1">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-full">
              <div className="px-4 py-5 sm:px-6 sm:py-6 space-y-8">
                <section className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    {t('contratoForm.sections.personal')}
                  </h2>
                  <TextField label={t('contratoForm.nome')} value={formData.nome} onChange={(v) => patch('nome', v)} required autoComplete="given-name" />
                  <TextField label={t('contratoForm.sobrenome')} value={formData.sobrenome} onChange={(v) => patch('sobrenome', v)} required autoComplete="family-name" />
                  <TextField label={t('contratoForm.dataNascimento')} type="date" value={formData.dataNascimento} onChange={(v) => patch('dataNascimento', v)} required />
                  <TextField label={t('contratoForm.nacionalidade')} value={formData.nacionalidade} onChange={(v) => patch('nacionalidade', v)} />
                  <TextField label={t('contratoForm.email')} type="email" value={formData.email} onChange={(v) => patch('email', v)} required autoComplete="email" />
                  <TextField label={t('contratoForm.telefone')} value={formData.telefone} onChange={(v) => patch('telefone', v)} autoComplete="tel" />
                </section>

                <section className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    {t('contratoForm.sections.document')}
                  </h2>
                  <label className="visa-form-field block">
                    <FieldLabel required>{t('contratoForm.documentoTipo')}</FieldLabel>
                    <select
                      value={formData.documentoTipo}
                      onChange={(e) => patch('documentoTipo', e.target.value as DocumentoTipo)}
                      className="visa-form-input"
                    >
                      <option value="passaporte">{t('contratoForm.documentoTipoOptions.passaporte')}</option>
                      <option value="cpf">{t('contratoForm.documentoTipoOptions.cpf')}</option>
                      <option value="nif">{t('contratoForm.documentoTipoOptions.nif')}</option>
                    </select>
                  </label>
                  <TextField
                    label={t('contratoForm.documentoNumero')}
                    value={formData.passaporteNumero}
                    onChange={(v) => patch('passaporteNumero', v)}
                    required
                  />
                  {formData.documentoTipo === 'passaporte' && (
                    <TextField
                      label={t('contratoForm.passaporteValidade')}
                      type="date"
                      value={formData.passaporteValidade}
                      onChange={(v) => patch('passaporteValidade', v)}
                      required
                    />
                  )}
                </section>

                <section className="space-y-4">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                    {t('contratoForm.sections.address')}
                  </h2>
                  <TextField label={t('contratoForm.logradouro')} value={formData.endereco.logradouro} onChange={(v) => patchEndereco('logradouro', v)} required />
                  <TextField label={t('contratoForm.numero')} value={formData.endereco.numero} onChange={(v) => patchEndereco('numero', v)} required />
                  <TextField label={t('contratoForm.complemento')} value={formData.endereco.complemento} onChange={(v) => patchEndereco('complemento', v)} />
                  <TextField label={t('contratoForm.bairro')} value={formData.endereco.bairro} onChange={(v) => patchEndereco('bairro', v)} required />
                  <TextField label={t('contratoForm.cidade')} value={formData.endereco.cidade} onChange={(v) => patchEndereco('cidade', v)} required />
                  <TextField label={t('contratoForm.cep')} value={formData.endereco.cep} onChange={(v) => patchEndereco('cep', v)} required />
                  <TextField label={t('contratoForm.pais')} value={formData.endereco.pais} onChange={(v) => patchEndereco('pais', v)} required />
                </section>

                <section className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-white px-4 py-3.5 min-h-[52px] has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
                    <input
                      type="checkbox"
                      checked={formData.consentLGPD}
                      onChange={(e) => patch('consentLGPD', e.target.checked)}
                      className="check-input mt-1 shrink-0"
                    />
                    <span className="font-medium text-gray-900">{t('contratoForm.consentAccept')}</span>
                  </label>
                </section>

                {formError && (
                  <p className="text-base sm:text-sm font-medium text-red-600" role="alert">
                    {formError}
                  </p>
                )}

                <Button className="w-full"
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('contratoForm.submit')}
                </Button>
              </div>
            </div>

            <p className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6 px-2">
              {t('contratoForm.privacy')}
            </p>
          </div>

          <aside className="hidden lg:flex shrink-0 sticky top-8 self-start flex-col items-center">
            <LanguageSwitcher orientation="vertical" variant="floating" compact />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ContratoFormFill;
